import { useTranslation } from 'react-i18next';

/**
 * ResultStep - Final step showing verification result
 */
function ResultStep({ result, onContinue, onRetry, onContactSupport }) {
  const { t } = useTranslation();
  const isSuccess = result?.isValid;
  const confidenceLevelRaw = String(result?.livenessConfidence || '').trim().toUpperCase();
  const confidenceLabel = (() => {
    if (!confidenceLevelRaw) {
      return t('verification.liveness-confidence-unknown', { defaultValue: 'N/A' });
    }
    if (confidenceLevelRaw === 'HIGH') {
      return t('verification.liveness-confidence-high', { defaultValue: 'Alta' });
    }
    if (confidenceLevelRaw === 'MEDIUM') {
      return t('verification.liveness-confidence-medium', { defaultValue: 'Media' });
    }
    if (confidenceLevelRaw === 'LOW') {
      return t('verification.liveness-confidence-low', { defaultValue: 'Baja' });
    }
    return result?.livenessConfidence;
  })();

  return (
    <div className="space-y-6 text-center">
      <div className={`text-4xl ${isSuccess ? 'text-green-600' : 'text-red-600'}`}>
        {isSuccess ? '✓' : '✗'}
      </div>

      <div>
        <h3 className={`text-xl font-bold mb-2 ${isSuccess ? 'text-green-900' : 'text-red-900'}`}>
          {isSuccess
            ? t('verification.success-title', { defaultValue: 'Verificación Exitosa' })
            : t('verification.failed-title', { defaultValue: 'Verificación Fallida' })}
        </h3>
        <p className="text-sm text-gray-600">
          {isSuccess
            ? t('verification.success-message', {
                defaultValue: 'Tu identidad ha sido verificada exitosamente.'
              })
            : t('verification.failed-message', {
                defaultValue: 'No pudimos verificar tu identidad. Por favor intenta de nuevo o contacta soporte.'
              })}
        </p>
      </div>

      {result && (
        <div className="amplify-card p-4 bg-gray-50 rounded-lg text-left">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">
                {t('verification.similarity-score', { defaultValue: 'Puntuación de Similitud:' })}
              </span>
              <span className="font-semibold">{Math.round(result.similarity || 0)}%</span>
            </div>
            {result.livenessScore !== undefined && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {t('verification.liveness-score', { defaultValue: 'Puntuación de Vida:' })}
                  </span>
                  <span className="font-semibold">{Math.round(result.livenessScore || 0)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {t('verification.liveness-confidence', { defaultValue: 'Confianza:' })}
                  </span>
                  <span className="font-semibold">{confidenceLabel}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {isSuccess ? (
          <button
            type="button"
            className="amplify-button amplify-button--primary w-full"
            onClick={onContinue}
          >
            {t('verification.continue-to-sign', {
              defaultValue: 'Continuar a Firmar'
            })}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="amplify-button amplify-button--primary w-full"
              onClick={onRetry}
            >
              {t('verification.retry-button', {
                defaultValue: 'Intentar de Nuevo'
              })}
            </button>
            <button
              type="button"
              className="amplify-button amplify-button--outlined w-full"
              onClick={onContactSupport}
            >
              {t('verification.contact-support', {
                defaultValue: 'Contactar Soporte'
              })}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ResultStep;
